import { useEffect, useMemo, useState } from "react";
import {
  clearAdminSession,
  getPlannerConfig,
  isAdminAuthenticated,
  savePlannerRate,
  uploadPlannerRateImage,
} from "../services/adminApi";
import "./CataloguePage.css";
import "./PlannerRateImages.css";

const ROOMS = [
  ["master", "Master bedroom"],
  ["child", "Child bedroom"],
  ["guest", "Guest bedroom"],
  ["kitchen", "Kitchen"],
  ["living", "Living room"],
  ["dining", "Dining area"],
  ["pooja", "Pooja unit"],
  ["entrance", "Entrance"],
  ["terrace", "Terrace"],
  ["electrical", "Electrical work"],
];
const BEDROOM_KEYS = new Set(["bedside:standard"]);
const UNITS = [
  ["SQ.FT", "Square feet (SQ.FT)"],
  ["RFT", "Running feet (RFT)"],
  ["FT", "Feet (FT)"],
  ["MTR", "Metre (MTR)"],
  ["EACH", "Each (EACH)"],
  ["NO", "Per item (NO)"],
  ["SET", "Per set (SET)"],
  ["APPROX", "Approximate item (APPROX)"],
];
const BED_TYPES = [
  "Standard",
  "Storage bed",
  "Hydraulic storage",
  "Upholstered",
];
const SOFA_TYPES = ["3-seater", "3+2 set", "L-shape sectional", "3+2+1 set"];
const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
const unitLabel = (unit) =>
  UNITS.find(([value]) => value === String(unit || "").toUpperCase())?.[1] ||
  unit ||
  "Unit";
const imageUrl = (url) => {
  if (!url || /^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  const base = (
    process.env.REACT_APP_ADMIN_API_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    "http://localhost:3005"
  ).replace(/\/$/, "");
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
};
const isVisualRate = (rate) =>
  /^(bed|sofa):/.test(String(rate.planner_key || ""));
const isElectrical = (rate) =>
  rate.rate_group === "electrical" ||
  String(rate.planner_key).startsWith("electrical:");
const rateFormula = (rate) =>
  ({
    room_area: "Room length × room width",
    wall_area: "Room perimeter × wall height",
    dado_area: "Room perimeter × dado height",
    running_ft: "Kitchen layout running feet",
    bed_area: "Customer bed width × length",
    fixed_area: "Fixed item length × width",
    quantity: "Customer quantity × rate",
    fixed: "One item × rate",
    percentage: "Quotation total percentage",
    round_down: "Rounding amount",
  })[rate.calculation_type] || "Existing planner formula";

function grouping(config, roomType) {
  const rates = new Map(config.rates.map((rate) => [String(rate.id), rate]));
  const groups = new Map();
  const add = (title, rate) => {
    if (!rate) return;
    const current = groups.get(title) || [];
    if (!current.some((item) => String(item.id) === String(rate.id)))
      current.push(rate);
    groups.set(title, current);
  };
  config.bindings
    .filter(
      (binding) =>
        binding.room_type === roomType && binding.status === "active",
    )
    .forEach((binding) =>
      add(
        binding.question_key === "Add more"
          ? "Optional add-ons"
          : binding.question_key,
        rates.get(String(binding.planner_rate_item_id)),
      ),
    );
  config.rooms
    .filter((row) => row.room_type === roomType)
    .forEach((row) => {
      const rate = rates.get(String(row.planner_rate_item_id));
      if (rate?.rate_group === "civil") add("Civil work", rate);
    });
  if (["master", "child", "guest"].includes(roomType))
    config.rates
      .filter((rate) => BEDROOM_KEYS.has(rate.planner_key))
      .forEach((rate) => add("Bedside tables", rate));
  if (roomType === "kitchen")
    config.rates
      .filter((rate) => rate.planner_key === "kitchen:island-unit")
      .forEach((rate) => add("Kitchen extras", rate));
  return [...groups.entries()].map(([title, values]) => [
    title,
    values.sort((a, b) => Number(a.sort_order) - Number(b.sort_order)),
  ]);
}

function RateRow({ rate, onChange, onImageUpload, uploading }) {
  const electrical = isElectrical(rate);
  const unitOptions = UNITS.some(([value]) => value === rate.unit)
    ? UNITS
    : [...UNITS, [rate.unit, `${rate.unit} (current)`]];
  return (
    <article className="planner-rate-row">
      <div className="planner-rate-row__name">
        {isVisualRate(rate) && (
          <div
            className="planner-rate-row__image"
            style={{
              alignItems: "center",
              display: "flex",
              gap: 10,
              marginBottom: 5,
            }}
          >
            {rate.image_url ? (
              <img
                src={imageUrl(rate.image_url)}
                alt={`${rate.display_name} option`}
                style={{
                  borderRadius: 5,
                  height: 58,
                  objectFit: "cover",
                  width: 76,
                }}
              />
            ) : (
              <span
                style={{
                  alignItems: "center",
                  background: "#f8f2e9",
                  borderRadius: 5,
                  color: "#756d63",
                  display: "flex",
                  fontSize: 11,
                  height: 58,
                  justifyContent: "center",
                  width: 76,
                }}
              >
                No image
              </span>
            )}
            <label
              style={{
                color: "#6c6257",
                cursor: uploading ? "wait" : "pointer",
                fontSize: 12,
              }}
            >
              {uploading
                ? "Uploading…"
                : rate.image_url
                  ? "Change image"
                  : "Upload image"}
              <input
                style={{
                  display: "block",
                  fontSize: 11,
                  marginTop: 4,
                  maxWidth: 190,
                }}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                disabled={uploading}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) onImageUpload(rate, file);
                  event.target.value = "";
                }}
              />
            </label>
          </div>
        )}
        <strong>{rate.display_name}</strong>
        <span>{rateFormula(rate)}</span>
        <small>
          {rate.status === "active"
            ? "Available to customers"
            : "Hidden from new planner selections"}{" "}
          · Unit: {unitLabel(rate.unit)}
        </small>
      </div>
      <label>
        Supply / base rate
        <input
          type="number"
          min="0"
          step="0.01"
          value={rate.base_rate ?? ""}
          onChange={(event) => onChange("base_rate", event.target.value)}
        />
      </label>
      <label>
        Unit
        <select
          value={rate.unit}
          onChange={(event) => onChange("unit", event.target.value)}
        >
          {unitOptions.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label>
        Status
        <select
          value={rate.status === "inactive" ? "inactive" : "active"}
          onChange={(event) => onChange("status", event.target.value)}
        >
          <option value="active">Available to customers</option>
          <option value="inactive">Hidden</option>
        </select>
      </label>
      {electrical && (
        <label>
          Installation rate
          <input
            type="number"
            min="0"
            step="0.01"
            value={rate.installation_rate ?? ""}
            onChange={(event) =>
              onChange("installation_rate", event.target.value)
            }
          />
        </label>
      )}
      {electrical && (
        <label>
          Typical quantity
          <input
            type="number"
            min="0"
            step="1"
            value={rate.default_quantity ?? ""}
            onChange={(event) =>
              onChange("default_quantity", event.target.value)
            }
          />
        </label>
      )}
      <div className="planner-rate-row__total">
        <span>{electrical ? "Customer rate" : "Rate"}</span>
        <strong>
          {money(
            Number(rate.base_rate) +
              (electrical ? Number(rate.installation_rate || 0) : 0),
          )}{" "}
          / {unitLabel(rate.unit)}
        </strong>
      </div>
    </article>
  );
}

export default function CataloguePage() {
  const [config, setConfig] = useState({ rates: [], bindings: [], rooms: [] });
  const [activeRoom, setActiveRoom] = useState("master");
  const [dirty, setDirty] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [uploadingImageId, setUploadingImageId] = useState(null);
  const load = async () => {
    setLoading(true);
    try {
      setConfig(await getPlannerConfig());
      setDirty(new Set());
    } catch (error) {
      setMessage(
        error.message || "Planner Rate Master is not initialized yet.",
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!isAdminAuthenticated()) window.location.replace("/admin/login");
    else load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const roomGroups = useMemo(
    () => grouping(config, activeRoom),
    [config, activeRoom],
  );
  const settings = useMemo(
    () =>
      config.rates.filter(
        (rate) => rate.rate_group === "setting" && rate.status === "active",
      ),
    [config],
  );
  const updateRate = (id, key, value) => {
    setConfig((current) => ({
      ...current,
      rates: current.rates.map((rate) =>
        String(rate.id) === String(id) ? { ...rate, [key]: value } : rate,
      ),
    }));
    setDirty((current) => new Set([...current, String(id)]));
  };
  const uploadImage = async (rate, file) => {
    setUploadingImageId(rate.id);
    setMessage("");
    try {
      const result = await uploadPlannerRateImage(rate.id, file);
      setConfig((current) => ({
        ...current,
        rates: current.rates.map((item) =>
          String(item.id) === String(rate.id)
            ? {
                ...item,
                image_url: result.image_url,
                image_url_override: result.image_url,
              }
            : item,
        ),
      }));
      setMessage(`Image saved for ${rate.display_name}.`);
    } catch (error) {
      setMessage(error.message || "Unable to upload planner option image.");
    } finally {
      setUploadingImageId(null);
    }
  };
  const save = async () => {
    if (!dirty.size) return;
    setSaving(true);
    setMessage("");
    try {
      await Promise.all(
        config.rates
          .filter((rate) => dirty.has(String(rate.id)))
          .map((rate) =>
            savePlannerRate({
              ...rate,
              base_rate: Number(rate.base_rate),
              installation_rate: Number(rate.installation_rate || 0),
              default_quantity:
                rate.default_quantity === "" || rate.default_quantity == null
                  ? null
                  : Number(rate.default_quantity),
            }),
          ),
      );
      setDirty(new Set());
      setMessage(
        "Rates saved. New FurniturePlanner quotations will use these prices.",
      );
    } catch (error) {
      setMessage(error.message || "Unable to save rates.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <main className="planner-master-page">
      <header className="planner-master-head">
        <div>
          <p>Dwellora Designs</p>
          <h1>Furniture Planner Pricing</h1>
          <span>
            Select a room, then set the customer-facing rates used by its
            quotation formula.
          </span>
        </div>
        <div>
          <a href="/admin">Dashboard</a>
          <a href="/admin/quote-pricing">Quotation Tool pricing</a>
          <button
            type="button"
            onClick={() => {
              clearAdminSession();
              window.location.replace("/admin/login");
            }}
          >
            Logout
          </button>
        </div>
      </header>
      {message && (
        <p
          className={`planner-message${message.includes("saved") ? "" : " planner-message--error"}`}
        >
          {message}
        </p>
      )}
      <section className="planner-help">
        <strong>How this works</strong>
        <span>
          Set the rate, unit, and whether each option is available to customers.
          NO means one individual item (for example, one door); SQ.FT means each
          square foot; RFT means each running foot; SET means one complete set.
          Hidden options are excluded from new planner sessions. Upload an
          image for each bed type or sofa design to show it to customers.
        </span>
      </section>
      <nav className="planner-room-tabs" aria-label="Planner rooms">
        {ROOMS.map(([key, label]) => (
          <button
            type="button"
            key={key}
            className={activeRoom === key ? "is-active" : ""}
            onClick={() => setActiveRoom(key)}
          >
            {label}
          </button>
        ))}
      </nav>
      <section className="planner-rate-panel">
        <div className="planner-panel-title">
          <div>
            <p>Room pricing</p>
            <h2>{ROOMS.find(([key]) => key === activeRoom)?.[1]}</h2>
            <span>
              These are the prices customers receive when they choose options in
              this room.
            </span>
          </div>
          <button type="button" disabled={!dirty.size || saving} onClick={save}>
            {saving
              ? "Saving…"
              : `Save ${dirty.size || ""} change${dirty.size === 1 ? "" : "s"}`}
          </button>
        </div>
        {loading && <p className="planner-state">Loading your pricing…</p>}
        {!loading && !roomGroups.length && (
          <p className="planner-state">
            No rate items are assigned to this room yet. Import the Planner Rate
            Master SQL file once, then reload this page.
          </p>
        )}
        {!loading &&
          roomGroups.map(([title, rates]) => (
            <section className="planner-rate-group" key={title}>
              <h3>{title}</h3>
              <div>
                {rates.map((rate) => (
                  <RateRow
                    key={rate.id}
                    rate={rate}
                    onChange={(field, value) =>
                      updateRate(rate.id, field, value)
                    }
                    onImageUpload={uploadImage}
                    uploading={String(uploadingImageId) === String(rate.id)}
                  />
                ))}
              </div>
            </section>
          ))}
      </section>
      <section className="planner-settings-panel">
        <div>
          <p>Whole quotation</p>
          <h2>Quotation settings</h2>
          <span>These settings apply after all room items are totalled.</span>
        </div>
        <div className="planner-settings-grid">
          {settings.map((rate) => (
            <label key={rate.id}>
              {rate.planner_key === "setting:gst-percent"
                ? "GST %"
                : rate.planner_key === "setting:installation-percent"
                  ? "Architectural consultancy %"
                  : "Discount rounding (INR)"}
              <input
                type="number"
                min="0"
                step="0.01"
                value={rate.base_rate ?? ""}
                onChange={(event) =>
                  updateRate(rate.id, "base_rate", event.target.value)
                }
              />
            </label>
          ))}
        </div>
        <button type="button" disabled={!dirty.size || saving} onClick={save}>
          {saving ? "Saving…" : "Save all changes"}
        </button>
      </section>
    </main>
  );
}
